/**
 * Parsowanie DatoCMS Structured Text (DAST) z pola GraphQL `{ value }`.
 * Bez zależności od react-datocms — wystarczy na układ: nagłówek + akapity.
 */

function normalizeDocument(value) {
  if (value == null) return null;
  let v = value;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return null;
    }
  }
  if (v.document && typeof v.document === "object") return v.document;
  if (v.type === "root") return v;
  if (v.schema === "dast" && v.document) return v.document;
  return null;
}

function flattenText(node) {
  if (!node) return "";
  if (node.type === "span" || node.type === "text") {
    return node.value != null ? String(node.value) : "";
  }
  if (Array.isArray(node.children)) {
    return node.children.map(flattenText).join("");
  }
  return "";
}

/**
 * @param {unknown} value — `field.value` z Dato (obiekt DAST lub string JSON)
 * @returns {{ headline: string, paragraphs: string[] } | null}
 */
export function extractStructuredSection(value) {
  const doc = normalizeDocument(value);
  if (!doc?.children?.length) return null;

  let headline = "";
  const paragraphs = [];

  for (const node of doc.children) {
    if (!node?.type) continue;

    if (node.type === "heading") {
      const t = flattenText(node).trim();
      if (!t) continue;
      if (!headline) headline = t;
      else paragraphs.push(t);
      continue;
    }

    if (node.type === "paragraph") {
      const t = flattenText(node).trim();
      if (t) paragraphs.push(t);
      continue;
    }

    if (node.type === "list") {
      for (const item of node.children || []) {
        if (item?.type === "listItem") {
          const t = flattenText(item).trim();
          if (t) paragraphs.push(t);
        }
      }
      continue;
    }

    if (node.type === "blockquote") {
      const t = flattenText(node).trim();
      if (t) paragraphs.push(t);
    }
  }

  if (!headline && paragraphs.length > 0) {
    return { headline: paragraphs[0], paragraphs: paragraphs.slice(1) };
  }

  if (!headline && paragraphs.length === 0) return null;

  return { headline, paragraphs };
}

/**
 * Pierwszy sensowny blok: nagłówek + jeden string na „lead” (akapity połączone spacją).
 */
export function headlineAndLeadFromStructured(value) {
  const block = extractStructuredSection(value);
  if (!block) return { headline: "", sub: "" };
  const sub =
    block.paragraphs.length > 0 ? block.paragraphs.join(" ").trim() : "";
  return { headline: block.headline.trim(), sub };
}
